/**
 * CHIè Custom JavaScript
 * Enhances user experience with smooth animations and interactions
 */

class CHIeTheme {
  constructor() {
    this.init();
  }

  init() {
    this.setupScrollAnimations();
    this.setupSmoothScrolling();
    this.setupProductCardInteractions();
    this.setupFormEnhancements();
    this.setupLoadingStates();
  }

  // Scroll-based animations using Intersection Observer
  setupScrollAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('chie-in-view');

          // Add stagger effect for child elements
          if (entry.target.classList.contains('chie-stagger-parent')) {
            const children = entry.target.children;
            Array.from(children).forEach((child, index) => {
              setTimeout(() => {
                child.classList.add('chie-animate-fadeInUp');
              }, index * 100);
            });
          }
        }
      });
    }, observerOptions);

    // Observe elements with animation classes
    const animatedElements = document.querySelectorAll('.chie-animate-on-scroll');
    animatedElements.forEach(el => observer.observe(el));
  }

  // Smooth scrolling for internal links
  setupSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const target = document.querySelector(anchor.getAttribute('href'));
        if (target) {
          e.preventDefault();
          const headerHeight = document.querySelector('header')?.offsetHeight || 0;
          const targetPosition = target.offsetTop - headerHeight - 20;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  // Enhanced product card interactions
  setupProductCardInteractions() {
    const productCards = document.querySelectorAll('.product-card-chie, .card-chie');

    productCards.forEach(card => {
      // Add hover effects
      card.addEventListener('mouseenter', () => {
        card.classList.add('chie-hover-active');

        // Add subtle tilt effect
        const rect = card.getBoundingClientRect();
        card.style.transformOrigin = 'center center';
      });

      card.addEventListener('mouseleave', () => {
        card.classList.remove('chie-hover-active');
        card.style.transform = '';
      });

      // Add click ripple effect
      card.addEventListener('click', (e) => {
        const ripple = document.createElement('div');
        const rect = card.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;

        ripple.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          left: ${x}px;
          top: ${y}px;
          background: rgba(200, 90, 90, 0.3);
          border-radius: 50%;
          transform: scale(0);
          animation: ripple 0.6s linear;
          pointer-events: none;
          z-index: 1;
        `;

        card.style.position = 'relative';
        card.style.overflow = 'hidden';
        card.appendChild(ripple);

        setTimeout(() => {
          ripple.remove();
        }, 600);
      });
    });

    // Add ripple animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes ripple {
        to {
          transform: scale(4);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // Form enhancements
  setupFormEnhancements() {
    const forms = document.querySelectorAll('form');

    forms.forEach(form => {
      // Add loading state to submit buttons
      form.addEventListener('submit', (e) => {
        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
        if (submitBtn && !form.getAttribute('data-no-loading')) {
          submitBtn.classList.add('chie-loading');
          submitBtn.disabled = true;

          const originalText = submitBtn.textContent;
          submitBtn.textContent = 'Caricamento...';

          // Reset after 5 seconds if form doesn't redirect
          setTimeout(() => {
            submitBtn.classList.remove('chie-loading');
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }, 5000);
        }
      });

      // Enhance input fields
      const inputs = form.querySelectorAll('input, textarea, select');
      inputs.forEach(input => {
        // Add focus effects
        input.addEventListener('focus', () => {
          input.parentElement.classList.add('chie-input-focused');
        });

        input.addEventListener('blur', () => {
          input.parentElement.classList.remove('chie-input-focused');

          // Add filled class if input has value
          if (input.value) {
            input.parentElement.classList.add('chie-input-filled');
          } else {
            input.parentElement.classList.remove('chie-input-filled');
          }
        });

        // Check if input is pre-filled
        if (input.value) {
          input.parentElement.classList.add('chie-input-filled');
        }
      });
    });
  }

  // Loading states for async actions
  setupLoadingStates() {
    // Cart additions
    document.addEventListener('click', (e) => {
      if (e.target.matches('.btn-chie-primary[name="add"]') ||
          e.target.closest('.btn-chie-primary[name="add"]')) {
        const btn = e.target.matches('.btn-chie-primary') ?
                   e.target : e.target.closest('.btn-chie-primary');

        this.setLoadingState(btn, 'Aggiunta al carrello...');
      }
    });

    // Newsletter signup
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="contact"]') ||
          e.target.matches('.newsletter-form')) {
        const submitBtn = e.target.querySelector('button[type="submit"]');
        if (submitBtn) {
          this.setLoadingState(submitBtn, 'Iscrizione...');
        }
      }
    });
  }

  // Utility function to set loading state
  setLoadingState(element, loadingText) {
    element.classList.add('chie-loading');
    element.disabled = true;

    const originalText = element.textContent;
    element.textContent = loadingText;

    // Reset after 3 seconds if no other reset occurs
    setTimeout(() => {
      element.classList.remove('chie-loading');
      element.disabled = false;
      element.textContent = originalText;
    }, 3000);
  }

  // Parallax effect for hero sections
  setupParallaxEffect() {
    const parallaxElements = document.querySelectorAll('.chie-parallax');

    if (parallaxElements.length > 0) {
      let ticking = false;

      const updateParallax = () => {
        const scrolled = window.pageYOffset;

        parallaxElements.forEach(element => {
          const speed = element.dataset.parallaxSpeed || 0.5;
          const yPos = -(scrolled * speed);
          element.style.transform = `translate3d(0, ${yPos}px, 0)`;
        });

        ticking = false;
      };

      const requestTick = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(updateParallax);
        }
      };

      window.addEventListener('scroll', requestTick);
    }
  }

  // Lazy loading for images
  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.dataset.src;

            if (src) {
              img.src = src;
              img.classList.add('chie-loaded');
              imageObserver.unobserve(img);
            }
          }
        });
      });

      const lazyImages = document.querySelectorAll('img[data-src]');
      lazyImages.forEach(img => imageObserver.observe(img));
    }
  }

  // Product image zoom functionality
  setupImageZoom() {
    const productImages = document.querySelectorAll('.product-media img');

    productImages.forEach(img => {
      img.addEventListener('mousemove', (e) => {
        const rect = img.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        img.style.transformOrigin = `${x}% ${y}%`;
        img.style.transform = 'scale(1.5)';
      });

      img.addEventListener('mouseleave', () => {
        img.style.transform = 'scale(1)';
        img.style.transformOrigin = 'center center';
      });
    });
  }

  // Testimonial carousel auto-play
  setupTestimonialCarousel() {
    const carousel = document.querySelector('.testimonials-chie__grid');
    if (carousel && carousel.children.length > 3) {
      let currentIndex = 0;
      const testimonials = Array.from(carousel.children);

      const showTestimonial = (index) => {
        testimonials.forEach((testimonial, i) => {
          testimonial.style.display = i >= index && i < index + 3 ? 'block' : 'none';
        });
      };

      const nextTestimonial = () => {
        currentIndex = (currentIndex + 1) % (testimonials.length - 2);
        showTestimonial(currentIndex);
      };

      // Auto-advance every 5 seconds
      setInterval(nextTestimonial, 5000);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new CHIeTheme();
});

// Additional utility functions
window.CHIe = {
  // Smooth scroll to element
  scrollTo: (selector, offset = 0) => {
    const element = document.querySelector(selector);
    if (element) {
      const headerHeight = document.querySelector('header')?.offsetHeight || 0;
      const targetPosition = element.offsetTop - headerHeight - offset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  },

  // Show notification
  showNotification: (message, type = 'success', duration = 3000) => {
    const notification = document.createElement('div');
    notification.className = `chie-notification chie-notification--${type}`;
    notification.textContent = message;

    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10B981' : '#EF4444'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 10px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.2);
      z-index: 10000;
      animation: chie-fadeInRight 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'chie-fadeInRight 0.3s ease reverse';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, duration);
  },

  // Format price
  formatPrice: (price, currency = '€') => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `${currency}${numPrice.toFixed(2).replace('.', ',')}`;
  }
};

// Performance optimization
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    // Load non-critical features when browser is idle
    const theme = new CHIeTheme();
    theme.setupParallaxEffect();
    theme.setupLazyLoading();
    theme.setupImageZoom();
    theme.setupTestimonialCarousel();
  });
}